import React, { useId, useRef } from 'react';
import Portal from './Portal';
import { IconButton } from './Button';
import { Icon } from '../icons';
import { useScrollLock, useOnEscape, useFocusTrap } from './overlayHooks';
import { useT } from '../../i18n';

/**
 * Dialog — modal pop-up centered over a scrim.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void              — fired by scrim click, Esc, and close button
 *   title: ReactNode                 — renders the header + close button
 *   description: ReactNode           — optional sub-text under the title
 *   size: 'sm' | 'md' | 'lg' | 'xl'  — max width (default 'md')
 *   footer: ReactNode                — pinned action row
 *   closeOnScrim: boolean            — default true
 *   children: body content
 */
const Dialog = ({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  closeOnScrim = true,
  className = '',
  children,
}) => {
  const t = useT();
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  useScrollLock(open);
  useOnEscape(open, () => onClose?.());
  useFocusTrap(panelRef, open);

  if (!open) return null;

  return (
    <Portal>
      <div className="ps-scope ps-overlay" role="presentation">
        <div
          className="ps-overlay__scrim"
          onMouseDown={() => closeOnScrim && onClose?.()}
        />
        <div
          ref={panelRef}
          className={`ps-dialog ps-dialog--${size} ${className}`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
        >
          {title && (
            <header className="ps-dialog__header">
              <div className="ps-dialog__heading">
                <h2 className="ps-dialog__title" id={titleId}>{title}</h2>
                {description && (
                  <p className="ps-dialog__desc" id={descId}>{description}</p>
                )}
              </div>
              <IconButton
                aria-label={t('Close')}
                variant="ghost"
                size="icon-sm"
                className="ps-dialog__close"
                onClick={() => onClose?.()}
              >
                <Icon.X />
              </IconButton>
            </header>
          )}
          <div className="ps-dialog__body">{children}</div>
          {footer && <footer className="ps-dialog__footer">{footer}</footer>}
        </div>
      </div>
    </Portal>
  );
};

export default Dialog;
